import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

export interface ErrorNavigationOptions {
    errorCode?: '404' | '403' | '500' | 'generic';
    errorTitle?: string;
    errorMessage?: string;
}

/**
 * Custom hook for navigating to the error page with proper state
 */
export const useErrorNavigation = () => {
    const navigate = useNavigate();
    
    const navigateToError = useCallback((options: ErrorNavigationOptions = {}) => {
        navigate('/error', {
            state: {
                errorCode: options.errorCode || 'generic',
                errorTitle: options.errorTitle,
                errorMessage: options.errorMessage
            }
        });
    }, [navigate]);
    
    const navigateToNotFound = useCallback((resourceType?: string, resourceId?: string) => {
        let errorMessage = "The page you're looking for doesn't exist or has been moved.";
        
        if (resourceType && resourceId) {
            errorMessage = `The ${resourceType} with ID "${resourceId}" could not be found or you don't have permission to access it.`;
        } else if (resourceType) {
            errorMessage = `The ${resourceType} could not be found or you don't have permission to access it.`;
        }
        
        navigateToError({
            errorCode: '404',
            errorTitle: resourceType ? `${resourceType.charAt(0).toUpperCase() + resourceType.slice(1)} not found` : 'Page not found',
            errorMessage
        });
    }, [navigateToError]);
    
    const navigateToAccessDenied = useCallback((resourceType?: string) => {
        navigateToError({
            errorCode: '403',
            errorTitle: 'Access denied',
            errorMessage: resourceType 
                ? `You don't have permission to access this ${resourceType}.`
                : "You don't have permission to access this resource."
        });
    }, [navigateToError]);
    
    const navigateToServerError = useCallback((errorDetails?: string) => {
        navigateToError({
            errorCode: '500',
            errorTitle: 'Server error',
            errorMessage: errorDetails || 'An unexpected error occurred. Please try again later.'
        });
    }, [navigateToError]);
    
    return {
        navigateToError,
        navigateToNotFound,
        navigateToAccessDenied,
        navigateToServerError
    };
};
